// ABOUTME: Native checkbox component styled to match the previous Radix-based design.
// ABOUTME: Replaced Radix primitive to avoid React 19 infinite loop (error #185).

import * as React from "react"
import { Check } from "lucide-react"
import { cn } from "@shared/lib/utils"

export interface CCheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "onChange" | "checked"
> {
  checked?: boolean | "indeterminate"
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  color?: "default" | "success" | "failure"
  /** Alias for `checked` kept for backward compat with Radix API */
  onCheckedChange?: (checked: boolean) => void
  /** onClick for the box: fires once per user click, whether it lands on the input or the label — typed loosely for backward compat */
  onClick?: React.MouseEventHandler<HTMLElement>
}

export const CCheckbox = React.forwardRef<HTMLInputElement, CCheckboxProps>(
  (
    {
      className,
      onChange,
      onCheckedChange,
      checked,
      defaultChecked,
      color,
      id,
      name,
      value,
      disabled,
      onClick,
      ...props
    },
    _ref,
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null)
    const combinedRef = (node: HTMLInputElement | null) => {
      innerRef.current = node
      if (typeof _ref === "function") _ref(node)
      else if (_ref) _ref.current = node
    }

    const colorClasses = {
      default: "",
      success: "border-green-500",
      failure: "border-red-500",
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      onCheckedChange?.(e.target.checked)
    }

    // A click on the label (its border lies outside the input) bubbles to the row behind the box before
    // the label's activation click ever reaches the input, so onClick must run on the label click itself.
    // The activation click the browser then dispatches on the input bubbles back here: onClick is not run
    // again (it ran once already), but if it stopped the first click's propagation the activation click is
    // stopped too, so the row sees neither. Activation is synchronous with the label click's default
    // action; the microtask only clears the flag when no activation follows (e.g. a disabled input).
    const pendingActivation = React.useRef<{ stopPropagation: boolean } | null>(null)
    const handleClick = (e: React.MouseEvent<HTMLLabelElement>) => {
      if (e.target === innerRef.current) {
        const pending = pendingActivation.current
        if (pending) {
          pendingActivation.current = null
          if (pending.stopPropagation) e.stopPropagation()
          return
        }
        onClick?.(e)
        return
      }
      onClick?.(e)
      pendingActivation.current = { stopPropagation: e.isPropagationStopped() }
      queueMicrotask(() => {
        pendingActivation.current = null
      })
    }

    const isIndeterminate = checked === "indeterminate"
    const isChecked = isIndeterminate ? false : (checked ?? undefined)
    const isActive = checked === true || isIndeterminate

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- the label only routes clicks to onClick once; the keyboard-accessible control is the <input> inside it
      <label
        htmlFor={id}
        onClick={handleClick}
        className={cn(
          "inline-grid place-content-center align-text-bottom peer h-4 w-4 shrink-0 rounded border border-gray-300 bg-white cursor-pointer",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-gray-400",
          disabled && "cursor-not-allowed opacity-50",
          isActive && "bg-inverse border-inverse text-inverse-foreground",
          color && colorClasses[color],
          isActive && color === "success" && "bg-green-500 border-green-500",
          isActive && color === "failure" && "bg-red-500 border-red-500",
          className,
        )}
      >
        <input
          ref={combinedRef}
          type="checkbox"
          id={id}
          name={name}
          value={value}
          checked={isChecked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={handleChange}
          className="sr-only"
          {...props}
        />
        {isIndeterminate ? (
          <div className="h-1.5 w-1.5 rounded-sm bg-inverse-foreground" />
        ) : checked === true ? (
          <Check
            className="h-3.5 w-3.5"
            strokeWidth={3}
          />
        ) : null}
      </label>
    )
  },
)

CCheckbox.displayName = "CCheckbox"
